import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import Header from 'components/AppBar';
import SubmitButton from 'components/Buttons/SubmitButton';
import BottomSheet from 'components/ConfirmBottomSheet';
import Field from 'components/Field';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { extractErrorMessage } from 'utils/generic';

import { TripPreference } from '../../../services/preferenceService';
import BedSelector from './components/BedSelector';
import RoomPreferenceSelector from './components/RoomPreferenceSelector';
import SeatSelector from './components/SeatSelector';
import { Container, ImageContainer, FormContainer, InfoContainer, InfoLabel } from './styles';

interface Props {
  preference?: TripPreference;
  isLoading: boolean;
  save: (preference: TripPreference) => Promise<void>;
}

const TravelerTripPreferences = ({ preference, save, isLoading }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const toast = useToast();
  const [data, setData] = useState<TripPreference>();
  const [confirm, setConfirm] = useState(false);

  const validationSchema = yup.object().shape({
    seatType: yup.string(),
    roomPreferenceType: yup.string(),
    bedType: yup.string(),
  });

  const initValues = {
    seatType: preference?.seatType || '',
    roomPreferenceType: preference?.roomPreferenceType || '',
    bedType: preference?.bedType || '',
  };

  const handleConfirm = (values: TripPreference) => {
    setData(values);
    setConfirm(true);
  };

  const handleSave = async () => {
    try {
      if (!data) return;

      await save(data);
      setConfirm(false);
      history.goBack();
    } catch (error) {
      setConfirm(false);
      window.scrollTo(0, 0);
      toast.add({
        type: 'danger',
        message: extractErrorMessage(error) || t('defaultError.default'),
      });
    }
  };

  return (
    <>
      {isLoading ? (
        <Loader fullScreen />
      ) : (
        <>
          <Mobile>
            <Header goBack={() => history.goBack()}>{t('preferences.title')}</Header>
          </Mobile>

          <Desktop>
            <ModalHeader onClose={() => history.goBack()} title={t('preferences.title')} />
          </Desktop>

          <Container>
            <ImageContainer>
              <img src="/corporate/images/ilustra-aviao.svg" alt="" />
            </ImageContainer>

            <Formik
              initialStatus={{ rightCheckIfIsValid: true }}
              initialValues={initValues}
              validationSchema={validationSchema}
              onSubmit={(values) => handleConfirm(values as TripPreference)}
              isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
            >
              {({ dirty }: FormikValues) => (
                <>
                  <FormContainer>
                    <div>
                      <Field name="seatType" labelKey="preferences.trip.seatType">
                        <SeatSelector id="seatType" />
                      </Field>
                      <Field name="roomPreferenceType" labelKey="preferences.trip.roomPreferenceType">
                        <RoomPreferenceSelector id="roomPreferenceType" />
                      </Field>
                      <Field name="bedType" labelKey="preferences.trip.bedType">
                        <BedSelector id="bedType" />
                      </Field>
                    </div>

                    <InfoContainer>
                      <InfoLabel>
                        <strong>{t('preferences.trip.important')} </strong>
                        {t('preferences.trip.importantMessage')}
                      </InfoLabel>
                    </InfoContainer>

                    <SubmitButton dirty={dirty} isEdit>
                      {t('preferences.trip.saveBtn')}
                    </SubmitButton>
                  </FormContainer>
                </>
              )}
            </Formik>
            <BottomSheet
              show={confirm}
              title={t('preferences.trip.modalConfirmTitle')}
              content={t('preferences.trip.modalConfirmTravelerText')}
              onClose={() => setConfirm(false)}
              onConfirm={handleSave}
            />
          </Container>
        </>
      )}
    </>
  );
};

export default TravelerTripPreferences;
